/**
 * FFS Calculation Table Components
 *
 * Reusable table components for displaying FFS calculation values in both
 * React (editable view) and PDF (generated report) formats.
 *
 * For PDF rendering, uses FormulaText component to properly render
 * subscripts and superscripts since @react-pdf/renderer doesn't support
 * unicode subscript/superscript characters well with standard fonts.
 */

import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  FfsFieldSection,
  type FfsFormulaContext,
  getFieldDefinition,
  SECTION_FIELD_KEYS,
  SECTION_TITLES,
} from 'pages/reporting/reports/ffs/ffsFieldDefinitions'
import React from 'react'
import { GeckoMeasurement } from 'types'

import { FormulaPart, FormulaText, parseFormula } from './FormulaText'

const { Title } = Typography

// ==================== Types ====================

export interface CalculationTableRow {
  key: string
  /** Raw label string (formula syntax) */
  parameterRaw: string
  /** Parsed parameter parts for PDF rendering */
  parameterParts: FormulaPart[]
  /** Raw formula string (formula syntax) */
  formulaRaw: string
  /** Parsed formula parts for PDF rendering */
  formulaParts: FormulaPart[]
  /** Display value */
  value: string
}

/** Legacy interface for React table display */
export interface CalculationTableRowDisplay {
  key: string
  parameter: string
  formula: string
  value: string
}

export interface FfsCalculationTableProps {
  /** Section to display */
  section: FfsFieldSection
  /** Data object containing the field values */
  data: Record<string, GeckoMeasurement | null | boolean | string>
  /** Optional title override */
  title?: string
  /** Optional context for selecting correct formula variants */
  ffsPart?: 'part-4' | 'part-5'
  ffsLevel?: '1' | '2'
  indicationType?: string
}

// ==================== Helpers ====================

/**
 * Format a GeckoMeasurement to a display string
 */
function formatMeasurement(
  measurement: GeckoMeasurement | null | undefined
): string {
  if (!measurement) return '-'
  // Format number to reasonable precision
  const num = measurement.num
  const formatted =
    Math.abs(num) < 0.01 || Math.abs(num) >= 10000
      ? num.toExponential(3)
      : num.toFixed(3).replace(/\.?0+$/, '')
  return `${formatted} ${measurement.unit.id}`
}

/**
 * Get display value for any field value type
 */
function getDisplayValue(value: unknown): string {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'string') return value || '-'
  if (typeof value === 'object' && 'num' in value && 'unit' in value) {
    return formatMeasurement(value as GeckoMeasurement)
  }
  return String(value)
}

/**
 * Convert formula syntax to display string for React/HTML
 * Converts _x to subscript and ^x to superscript using HTML-like notation
 */
function formulaToDisplayString(formula: string): string {
  // Map of Greek letter names to Unicode symbols
  const greekLetters: Record<string, string> = {
    lambda: 'λ',
    alpha: 'α',
    beta: 'β',
    gamma: 'γ',
    delta: 'δ',
    epsilon: 'ε',
    theta: 'θ',
    sigma: 'σ',
    pi: 'π',
    omega: 'ω',
    sqrt: '√',
  }

  let result = formula

  // Replace Greek letter names with symbols
  for (const [name, symbol] of Object.entries(greekLetters)) {
    result = result.replace(new RegExp(`\\b${name}\\b`, 'g'), symbol)
  }

  // Convert subscripts: _{xyz} -> subscript unicode approximation
  // For HTML display, we'll keep it simple
  result = result.replace(/_{([^}]+)}/g, (_, content) => {
    // Convert to subscript unicode characters where possible
    const subscriptMap: Record<string, string> = {
      '0': '₀',
      '1': '₁',
      '2': '₂',
      '3': '₃',
      '4': '₄',
      '5': '₅',
      '6': '₆',
      '7': '₇',
      '8': '₈',
      '9': '₉',
      a: 'ₐ',
      e: 'ₑ',
      h: 'ₕ',
      i: 'ᵢ',
      j: 'ⱼ',
      k: 'ₖ',
      l: 'ₗ',
      m: 'ₘ',
      n: 'ₙ',
      o: 'ₒ',
      p: 'ₚ',
      r: 'ᵣ',
      s: 'ₛ',
      t: 'ₜ',
      u: 'ᵤ',
      v: 'ᵥ',
      x: 'ₓ',
    }
    return content
      .split('')
      .map((c: string) => subscriptMap[c] || c)
      .join('')
  })

  // Convert single-char subscripts
  result = result.replace(/_([a-zA-Z0-9])/g, (_, char) => {
    const subscriptMap: Record<string, string> = {
      '0': '₀',
      '1': '₁',
      '2': '₂',
      '3': '₃',
      '4': '₄',
      '5': '₅',
      '6': '₆',
      '7': '₇',
      '8': '₈',
      '9': '₉',
      a: 'ₐ',
      e: 'ₑ',
      h: 'ₕ',
      i: 'ᵢ',
      j: 'ⱼ',
      k: 'ₖ',
      l: 'ₗ',
      m: 'ₘ',
      n: 'ₙ',
      o: 'ₒ',
      p: 'ₚ',
      r: 'ᵣ',
      s: 'ₛ',
      t: 'ₜ',
      u: 'ᵤ',
      v: 'ᵥ',
      x: 'ₓ',
      y: 'ᵧ',
    }
    return subscriptMap[char] || `_${char}`
  })

  // Convert superscripts: ^{xyz} -> superscript unicode approximation
  result = result.replace(/\^{([^}]+)}/g, (_, content) => {
    const superscriptMap: Record<string, string> = {
      '0': '⁰',
      '1': '¹',
      '2': '²',
      '3': '³',
      '4': '⁴',
      '5': '⁵',
      '6': '⁶',
      '7': '⁷',
      '8': '⁸',
      '9': '⁹',
      a: 'ᵃ',
      b: 'ᵇ',
      c: 'ᶜ',
      d: 'ᵈ',
      e: 'ᵉ',
      f: 'ᶠ',
      g: 'ᵍ',
      h: 'ʰ',
      i: 'ⁱ',
      j: 'ʲ',
      k: 'ᵏ',
      l: 'ˡ',
      m: 'ᵐ',
      n: 'ⁿ',
      o: 'ᵒ',
      p: 'ᵖ',
      r: 'ʳ',
      s: 'ˢ',
      t: 'ᵗ',
      u: 'ᵘ',
      v: 'ᵛ',
      w: 'ʷ',
      x: 'ˣ',
      y: 'ʸ',
      z: 'ᶻ',
      L: 'ᴸ',
      C: 'ᶜ',
    }
    return content
      .split('')
      .map((c: string) => superscriptMap[c] || c)
      .join('')
  })

  // Convert single-char superscripts
  result = result.replace(/\^([a-zA-Z0-9])/g, (_, char) => {
    const superscriptMap: Record<string, string> = {
      '0': '⁰',
      '1': '¹',
      '2': '²',
      '3': '³',
      '4': '⁴',
      '5': '⁵',
      '6': '⁶',
      '7': '⁷',
      '8': '⁸',
      '9': '⁹',
      a: 'ᵃ',
      b: 'ᵇ',
      c: 'ᶜ',
      d: 'ᵈ',
      e: 'ᵉ',
      f: 'ᶠ',
      g: 'ᵍ',
      h: 'ʰ',
      i: 'ⁱ',
      j: 'ʲ',
      k: 'ᵏ',
      l: 'ˡ',
      m: 'ᵐ',
      n: 'ⁿ',
      o: 'ᵒ',
      p: 'ᵖ',
      r: 'ʳ',
      s: 'ˢ',
      t: 'ᵗ',
      u: 'ᵘ',
      v: 'ᵛ',
      w: 'ʷ',
      x: 'ˣ',
      y: 'ʸ',
      z: 'ᶻ',
      L: 'ᴸ',
      C: 'ᶜ',
    }
    return superscriptMap[char] || `^${char}`
  })

  return result
}

/**
 * Build table rows for a section from data
 */
export function buildTableRows(
  section: FfsFieldSection,
  data: Record<string, GeckoMeasurement | null | boolean | string>,
  context?: FfsFormulaContext
): CalculationTableRow[] {
  const fieldKeys = SECTION_FIELD_KEYS[section] || []

  return fieldKeys
    .map((key) => {
      const definition = getFieldDefinition(key, section, context)
      if (!definition) return null

      const value = data[key]
      const formulaRaw = definition.formula || '-'

      return {
        key,
        parameterRaw: definition.label,
        parameterParts: parseFormula(definition.label),
        formulaRaw,
        formulaParts:
          formulaRaw === '-'
            ? [{ type: 'text' as const, value: '-' }]
            : parseFormula(formulaRaw),
        value: getDisplayValue(value),
      }
    })
    .filter((row): row is CalculationTableRow => row !== null)
}

/**
 * Build display rows for React table (with unicode conversion)
 */
export function buildDisplayRows(
  section: FfsFieldSection,
  data: Record<string, GeckoMeasurement | null | boolean | string>,
  context?: FfsFormulaContext
): CalculationTableRowDisplay[] {
  const fieldKeys = SECTION_FIELD_KEYS[section] || []

  return fieldKeys
    .map((key) => {
      const definition = getFieldDefinition(key, section, context)
      if (!definition) return null

      const value = data[key]

      return {
        key,
        parameter: formulaToDisplayString(definition.label),
        formula: definition.formula
          ? formulaToDisplayString(definition.formula)
          : '-',
        value: getDisplayValue(value),
      }
    })
    .filter((row): row is CalculationTableRowDisplay => row !== null)
}

// ==================== PDF Styles ====================

const pdfStyles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  title: {
    fontFamily: 'Manrope',
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 6,
    color: '#333',
  },
  table: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#000',
    borderStyle: 'solid',
  },
  headerRow: {
    flexDirection: 'row',
    backgroundColor: '#e0e0e0',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    borderBottomStyle: 'solid',
  },
  headerCell: {
    fontFamily: 'Manrope',
    padding: 6,
    fontSize: 9,
    fontWeight: 'bold',
    borderRightWidth: 1,
    borderRightColor: '#000',
    borderRightStyle: 'solid',
  },
  headerCellLast: {
    fontFamily: 'Manrope',
    padding: 6,
    fontSize: 9,
    fontWeight: 'bold',
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    borderBottomStyle: 'solid',
    minHeight: 20,
  },
  rowLast: {
    flexDirection: 'row',
    minHeight: 20,
  },
  cell: {
    padding: 5,
    borderRightWidth: 1,
    borderRightColor: '#ccc',
    borderRightStyle: 'solid',
    justifyContent: 'center',
  },
  cellLast: {
    padding: 5,
    justifyContent: 'center',
  },
  cellText: {
    fontFamily: 'Manrope',
    fontSize: 8,
  },
  // Column widths: Parameter (25%), Formula (45%), Value (30%)
  colParameter: {
    width: '25%',
  },
  colFormula: {
    width: '45%',
  },
  colValue: {
    width: '30%',
  },
})

// ==================== PDF Component ====================

/**
 * PDF version of the FFS Calculation Table
 * Renders a table with Parameter | Formula | Value columns
 * Uses FormulaText for proper subscript/superscript rendering
 */
export const FfsCalculationTablePDF: React.FC<FfsCalculationTableProps> = ({
  section,
  data,
  title,
  ffsPart,
  ffsLevel,
  indicationType,
}) => {
  const context: FfsFormulaContext | undefined =
    ffsPart && ffsLevel ? { ffsPart, ffsLevel, indicationType } : undefined
  const rows = buildTableRows(section, data, context)
  const tableTitle = title || SECTION_TITLES[section]

  if (rows.length === 0) {
    return null
  }

  return (
    <View style={pdfStyles.container} wrap={false}>
      {/* Table Title */}
      <Text style={pdfStyles.title}>{tableTitle}</Text>

      {/* Table */}
      <View style={pdfStyles.table}>
        {/* Header Row */}
        <View style={pdfStyles.headerRow}>
          <View style={[pdfStyles.headerCell, pdfStyles.colParameter]}>
            <Text>Parameter</Text>
          </View>
          <View style={[pdfStyles.headerCell, pdfStyles.colFormula]}>
            <Text>Formula</Text>
          </View>
          <View style={[pdfStyles.headerCellLast, pdfStyles.colValue]}>
            <Text>Value</Text>
          </View>
        </View>

        {/* Data Rows */}
        {rows.map((row, index) => {
          const isLast = index === rows.length - 1
          const rowStyle = isLast ? pdfStyles.rowLast : pdfStyles.row
          const cellStyleLast = isLast ? pdfStyles.cellLast : pdfStyles.cell

          return (
            <View key={row.key} style={rowStyle}>
              <View style={[pdfStyles.cell, pdfStyles.colParameter]}>
                <FormulaText parts={row.parameterParts} baseFontSize={9} />
              </View>
              <View style={[pdfStyles.cell, pdfStyles.colFormula]}>
                <FormulaText parts={row.formulaParts} baseFontSize={8} />
              </View>
              <View style={[cellStyleLast, pdfStyles.colValue]}>
                <Text style={pdfStyles.cellText}>{row.value}</Text>
              </View>
            </View>
          )
        })}
      </View>
    </View>
  )
}

// ==================== React Component ====================

/**
 * React (Ant Design) version of the FFS Calculation Table
 * Used in the editable report view
 */
export const FfsCalculationTableReact: React.FC<FfsCalculationTableProps> = ({
  section,
  data,
  title,
  ffsPart,
  ffsLevel,
  indicationType,
}) => {
  const context: FfsFormulaContext | undefined =
    ffsPart && ffsLevel ? { ffsPart, ffsLevel, indicationType } : undefined
  const rows = buildDisplayRows(section, data, context)
  const tableTitle = title || SECTION_TITLES[section]

  const columns: ColumnsType<CalculationTableRowDisplay> = [
    {
      title: 'Parameter',
      dataIndex: 'parameter',
      key: 'parameter',
      width: '25%',
    },
    {
      title: 'Formula',
      dataIndex: 'formula',
      key: 'formula',
      width: '45%',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: '30%',
    },
  ]

  if (rows.length === 0) {
    return null
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <Title level={5} style={{ marginBottom: 8 }}>
        {tableTitle}
      </Title>
      <Table
        columns={columns}
        dataSource={rows}
        pagination={false}
        size='small'
        bordered
      />
    </div>
  )
}

// ==================== Utility Types for Section Data ====================

/** Report data structure for extracting section data */
export interface FfsReportDataForExtraction {
  userParams?: Record<string, GeckoMeasurement | null | boolean | string>
  dataValues?: Record<string, GeckoMeasurement | null | boolean | string>
  mawp?: Record<string, GeckoMeasurement | null | boolean | string>
  calculatedValues?: Record<string, GeckoMeasurement | null | boolean | string>
  thicknessCriteria?: Record<string, GeckoMeasurement | null | boolean | string>
  flawSizeCriteria?: Record<string, GeckoMeasurement | null | boolean | string>
  finalAcceptabilityCriteria?: Record<
    string,
    GeckoMeasurement | null | boolean | string
  >
}

/**
 * Helper to extract data for a specific section from the full report
 */
export function extractSectionData(
  section: FfsFieldSection,
  reportData: FfsReportDataForExtraction
): Record<string, GeckoMeasurement | null | boolean | string> {
  switch (section) {
    case 'user_params':
      return (reportData.userParams ?? {}) as Record<
        string,
        GeckoMeasurement | null | boolean | string
      >
    case 'data_values':
      return (reportData.dataValues ?? {}) as Record<
        string,
        GeckoMeasurement | null | boolean | string
      >
    case 'mawp':
      return (reportData.mawp ?? {}) as Record<
        string,
        GeckoMeasurement | null | boolean | string
      >
    case 'calculated_values':
      return (reportData.calculatedValues ?? {}) as Record<
        string,
        GeckoMeasurement | null | boolean | string
      >
    case 'thickness_criteria':
      return (reportData.thicknessCriteria ?? {}) as Record<
        string,
        GeckoMeasurement | null | boolean | string
      >
    case 'flaw_size_criteria':
      return (reportData.flawSizeCriteria ?? {}) as Record<
        string,
        GeckoMeasurement | null | boolean | string
      >
    case 'final_acceptability_criteria':
      return (reportData.finalAcceptabilityCriteria ?? {}) as Record<
        string,
        GeckoMeasurement | null | boolean | string
      >
    default:
      return {}
  }
}
